import i18n from 'i18next';
import {Button, IconButton} from 'platform/components';
import {
  Box,
  getColorFromSeverity,
  HStack,
  Icon,
  Show,
  Text,
  VStack,
  type Severity,
  type ThemeIconKey,
} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {suffixTestId, type TestIdProps} from 'shared/web';

import type {BulkOperationResponseBody} from '../../types/api/bulkOperations';
import {type BulkOperationActionDefinitions} from '../../types/BulkOperationActionDefinition';
import type {BulkOperationPresentation} from '../../types/BulkOperationPresentation';
import {getBulkOperationActionDefinition} from '../../utils/getBulkOperationActionDefinition';
import {getBulkOperationProcessedCount} from '../../utils/getBulkOperationProcessedCount';
import {getBulkOperationRecoveryAction} from '../../utils/getBulkOperationRecoveryAction';
import {isBulkOperationTerminal} from '../../utils/isBulkOperationTerminal';
import {BulkOperationSubtitle} from '../BulkOperationSubtitle/BulkOperationSubtitle';
import {BulkOperationSeverityBar} from './BulkOperationSeverityBar';
import {getBulkOperationSeverity} from './utils/getBulkOperationSeverity';

type BulkOperationCardProps = TestIdProps & {
  operation: BulkOperationResponseBody;
  actionsDefinition: BulkOperationActionDefinitions;
  presentation?: BulkOperationPresentation;
  isRerunLoading: boolean;
  onViewDetail: () => void;
  onRerunFailed: () => void;
  onDismiss: () => void;
};

const STATUS_ICONS = {
  success: 'action/check_circle',
  warning: 'alert/error',
  danger: 'navigation/cancel',
  informational: 'action/info',
} satisfies Record<Severity, ThemeIconKey>;

/** One tracked operation inside the bulk operations panel. */
export function BulkOperationCard(props: BulkOperationCardProps) {
  const formatNumber = useFormatNumber();
  const operation = props.operation;
  const definition = getBulkOperationActionDefinition(
    operation.actionCode,
    props.actionsDefinition
  );
  const severity = getBulkOperationSeverity(operation);
  const isTerminal = isBulkOperationTerminal(operation.state);
  const processedCount = getBulkOperationProcessedCount(operation);
  const doneLabel =
    definition.doneLabel ??
    i18n.t('general.notifications.success').toLocaleLowerCase(i18n.resolvedLanguage);
  const showRerunFailed =
    isTerminal && getBulkOperationRecoveryAction(operation.state) === 'rerunFailed';

  return (
    <article aria-label={definition.title} className="grid grid-cols-[1rem_minmax(0,1fr)] gap-2.5">
      <div className="pt-2">
        <Icon value={STATUS_ICONS[severity]} color={getColorFromSeverity(severity)} size={4} />
      </div>
      <VStack spacing={2}>
        <HStack spacing={2} align="center">
          <Box flexGrow={1}>
            <VStack spacing={0}>
              <Text size="small">
                <strong>{definition.title}</strong>
              </Text>
              <BulkOperationSubtitle
                operation={operation}
                size="xSmall"
                presentation={props.presentation}
              />
            </VStack>
          </Box>
          <Show when={isTerminal}>
            <IconButton
              icon="navigation/close"
              size="small"
              aria-label={i18n.t('general.actions.close')}
              onClick={props.onDismiss}
              data-testid={suffixTestId('dismiss', props)}
            />
          </Show>
        </HStack>
        <BulkOperationSeverityBar operation={operation} />
        <HStack spacing={3} align="center" justify="space-between" wrap>
          {isTerminal ? (
            <Text size="small" color="secondary" data-testid={suffixTestId('summary', props)}>
              <strong className="text-primary">{formatNumber(operation.doneCount, 0)}</strong>{' '}
              {doneLabel} ·{' '}
              <strong className="text-primary">{formatNumber(operation.skippedCount, 0)}</strong>{' '}
              {i18n
                .t('entity.automationRule.detail.runResult.skipped')
                .toLocaleLowerCase(i18n.resolvedLanguage)}{' '}
              · <strong className="text-primary">{formatNumber(operation.failedCount, 0)}</strong>{' '}
              {i18n
                .t('general.notifications.labels.failed')
                .toLocaleLowerCase(i18n.resolvedLanguage)}
            </Text>
          ) : (
            <Text size="small" data-testid={suffixTestId('progress', props)}>
              {processedCount}/{operation.totalCount}
            </Text>
          )}
          <HStack spacing={2} align="center">
            <Button
              title={i18n.t('general.actions.viewDetail')}
              variant="link"
              size="small"
              onClick={props.onViewDetail}
              data-testid={suffixTestId('viewDetail', props)}
            />
            <Show when={showRerunFailed}>
              <Button
                title={i18n.t('general.actions.rerunFailed', {defaultValue: 'Re-run failed'})}
                variant="link"
                size="small"
                isLoading={props.isRerunLoading}
                isDisabled={props.isRerunLoading}
                onClick={props.onRerunFailed}
                data-testid={suffixTestId('rerunFailed', props)}
              />
            </Show>
          </HStack>
        </HStack>
      </VStack>
    </article>
  );
}
